'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import getTrailProfilePhotoUrl from '@alltrails/core/utils/getTrailProfilePhotoUrl';
import getCardPhotoUrl from '@alltrails/core/utils/getCardPhotoUrl';
import type Trail from '@alltrails/types/Trail';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';

const cardType = 'trails';

const usePhotos = (trail: Trail) => {
  const { id, defaultPhoto } = trail;
  const hasProfilePhoto = !!defaultPhoto;

  const [photos, setPhotos] = useState<string[]>([]);
  const [photosLoaded, setPhotosLoaded] = useState(false);
  const publicBaseUrl = usePublicBaseUrl();

  useEffect(() => {
    const initialPhoto = getCardPhotoUrl({ baseUrl: publicBaseUrl, cardType, id, index: 0, size: '500x500' });
    // Initial photo so that the carousel will render with pagination dots,
    // and when interacted with will then fetch and render the full list of photos
    const fillerPhotos = Array(5).fill(initialPhoto);
    setPhotos(fillerPhotos);
    setPhotosLoaded(false);
  }, [publicBaseUrl, id]);

  const loadPhotos = useCallback(() => {
    if (!photosLoaded) {
      setPhotosLoaded(true);

      const urls = photos.map((_, index) => {
        const url = getCardPhotoUrl({ baseUrl: publicBaseUrl, cardType, id, index, size: '500x500' });
        // pre-load images by browser
        try {
          new Image().src = url;
        } catch {
          // silently catch
        }
        return url;
      });

      setPhotos(urls);
    }
  }, [publicBaseUrl, photosLoaded, photos, id]);

  const photosToReturn = useMemo(
    // Fetch the static map directly as the fallback profile photo if there is no defaultPhoto:
    () =>
      hasProfilePhoto
        ? photos
        : [getTrailProfilePhotoUrl({ baseUrl: publicBaseUrl, id, size: '500x500', staticMapScale: 1, staticMapSize: '512x217' }), ...photos],
    [publicBaseUrl, hasProfilePhoto, photos, id]
  );

  return useMemo(() => ({ photos: photosToReturn, loadPhotos }), [loadPhotos, photosToReturn]);
};

export default usePhotos;
